import { __read } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useUpsellTrackingParam } from '@office-start/dime-service';
import { useFirstRunConfig } from '@office-start/first-run-config-service';
import FirstRunControl from '@office-start/first-run-control';
import { updateFirstRunState, updateSecondRunState, } from '@office-start/first-run-state-service';
import { firstRunVersion, RunType, secondRunVersion, } from '@office-start/first-run-types';
import { mark } from '@office-start/performance-service';
import { useThemeMode } from '@office-start/theming-service';
import React, { useEffect } from 'react';
import { handleBackgroundBlur } from './helpers/handleBackgroundBlur';
import { useShowDime } from './hooks/useShowDime';
import { useFirstRunVisibilityStore } from './store/store';
import { logDismissClick } from './telemetry/logDismissClick';
import { logFirstRunImpression } from './telemetry/logFirstRunImpression';
import { logFirstRunNavigation, } from './telemetry/logFirstRunNavigation';
import { logUpsellClick, secondRunLogUpsellClick, } from './telemetry/logUpsellClick';
var handleUpdateState = function (runType) {
    switch (runType) {
        case RunType.FirstRun:
            return updateFirstRunState({
                CompletedFirstRun: true,
                Version: firstRunVersion,
            });
        case RunType.SecondRun:
            return updateSecondRunState({
                CompletedFirstRun: true,
                Version: secondRunVersion,
            });
        case RunType.Disabled:
            return;
        default:
            var exhaustiveCheck = runType;
            throw new Error(exhaustiveCheck);
    }
};
export var FirstRunComponent = function (_a) {
    var _b = _a.runType, runType = _b === void 0 ? RunType.FirstRun : _b;
    var _c = useFirstRunVisibilityStore(), isVisible = _c.isVisible, setIsVisible = _c.setIsVisible;
    useEffect(function () {
        setIsVisible(true);
        return function () {
            setIsVisible(false);
        };
    }, [setIsVisible]);
    var _d = __read(React.useState(true), 2), isGreetingsActive = _d[0], setIsGreetingsActive = _d[1];
    var _e = __read(React.useState(0), 2), activeSlideIdx = _e[0], setActiveSlideIdx = _e[1];
    var _f = __read(React.useState(0), 2), activeSlideGroupIdx = _f[0], setActiveSlideGroupIdx = _f[1];
    var _g = useShowDime(), dimeActive = _g.dimeActive, dimeEnabled = _g.dimeEnabled, showDime = _g.showDime, userConvertedToPaid = _g.userConvertedToPaid;
    var OEMCode = useUpsellTrackingParam();
    var _h = __read(React.useState(false), 2), slideAnnouncmentEnabled = _h[0], setSlideAnnouncementEnabled = _h[1];
    var _j = __read(React.useState(''), 2), slideAnnouncement = _j[0], setSlideAnnouncement = _j[1];
    var isSecondRun = runType === RunType.SecondRun;
    var config = useFirstRunConfig(OEMCode, isSecondRun).data;
    var context = useOfficeComponentContext().data;
    var themeMode = useThemeMode();
    var activeSlide = (config === null || config === void 0 ? void 0 : config.slideGroups[activeSlideGroupIdx][activeSlideIdx]) || null;
    var slideGroupLength = (config === null || config === void 0 ? void 0 : config.slideGroups[activeSlideGroupIdx].length) || -1;
    var slideGroupsLength = (config === null || config === void 0 ? void 0 : config.slideGroups.length) || -1;
    var state = React.useMemo(function () { return ({
        activeSlide: activeSlide,
        activeSlideIdx: activeSlideIdx,
        activeSlideGroupIdx: activeSlideGroupIdx,
        dimeActive: dimeActive,
        dimeEnabled: dimeEnabled,
        isGreetingsActive: isGreetingsActive,
        isVisible: isVisible,
        slideAnnouncement: slideAnnouncement,
        slideAnnouncmentEnabled: slideAnnouncmentEnabled,
        slideGroupLength: slideGroupLength,
        userConvertedToPaid: userConvertedToPaid,
    }); }, [
        activeSlide,
        activeSlideIdx,
        activeSlideGroupIdx,
        dimeActive,
        dimeEnabled,
        isGreetingsActive,
        isVisible,
        slideAnnouncement,
        slideAnnouncmentEnabled,
        slideGroupLength,
        userConvertedToPaid,
    ]);
    React.useEffect(function () {
        mark('First_Run_Started');
        logFirstRunImpression();
        // Immediately mark first run completed in state object so user is not
        // exposed to first run on follow up visits if session closes early.
        // Initial state object is cached and served in ReactQuery for life of the
        // browser session so this update will not prematurely close the first run.
        // handleUpdateState function lets us dynamically update the state object depending on runType
        handleUpdateState(runType);
    }, [runType]);
    React.useEffect(function () {
        // activeSlideGroupIdx = 1 should hold generic paid user slides that
        // should be shown to a user who completes the Dime upsell flow.
        // This content should only be provided to ConsumerFree user types.
        if (userConvertedToPaid && slideGroupsLength >= 2) {
            setActiveSlideGroupIdx(1);
            setActiveSlideIdx(0);
        }
    }, [slideGroupsLength, userConvertedToPaid]);
    // functions
    var logNavigation = React.useCallback(function (action) {
        logFirstRunNavigation({
            action: action,
            firstRunType: (config === null || config === void 0 ? void 0 : config.firstRunType) || 'unknown',
            version: firstRunVersion,
            currentSlideIndex: activeSlideIdx,
            totalSlideLength: slideGroupLength,
        });
    }, [activeSlideIdx, config === null || config === void 0 ? void 0 : config.firstRunType, slideGroupLength]);
    var navigateNextSlide = React.useCallback(function (nextSlideIdx) {
        setActiveSlideIdx(nextSlideIdx);
        logNavigation('next');
    }, [logNavigation]);
    var navigatePreviousSlide = React.useCallback(function (previousSlideIdx) {
        setActiveSlideIdx(previousSlideIdx);
        logNavigation('back');
    }, [logNavigation]);
    var completeFirstRun = React.useCallback(function () {
        setIsVisible(false);
        setSlideAnnouncementEnabled(false);
        handleBackgroundBlur(false);
        logNavigation('complete');
        mark('First_Run_Complete');
    }, [logNavigation, setIsVisible]);
    // TODO: condense second run logic into logic that can run for any version
    var dismissSecondRunSlide = React.useCallback(function () {
        setIsVisible(false);
        setSlideAnnouncementEnabled(false);
        handleBackgroundBlur(false);
        // logs user dismiss clicks
        logDismissClick('Second_Run_Dismiss');
        mark('Second_Run_Dismiss');
    }, [setIsVisible]);
    var completeSecondRun = React.useCallback(function () {
        setIsVisible(false);
        setSlideAnnouncementEnabled(false);
        handleBackgroundBlur(false);
        logNavigation('secondRunComplete');
        mark('Second_Run_Complete');
    }, [logNavigation, setIsVisible]);
    var handleUpsellButtonClick = React.useCallback(function (activeSlide) {
        if (activeSlide.button === undefined) {
            return;
        }
        var link = activeSlide.button.link;
        if (dimeEnabled && showDime) {
            showDime(link);
            isSecondRun
                ? secondRunLogUpsellClick('Dime_Service')
                : logUpsellClick('Dime_Service');
        }
        else {
            import('@office-start/launcher-service').then(function (launchSvc) {
                launchSvc.launchUrl(link, {
                    action: isSecondRun ? 'SREAction' : 'FREAction',
                });
                isSecondRun
                    ? secondRunLogUpsellClick('Launcher_Service')
                    : logUpsellClick('Launcher_Service');
            });
        }
    }, [dimeEnabled, isSecondRun, showDime]);
    // handlers
    var handleFocusButtonRef = React.useCallback(function (ref) {
        if (ref === null || ref === void 0 ? void 0 : ref.current) {
            ref.current.focus();
        }
    }, []);
    var handleDisableGreetings = React.useCallback(function () {
        setIsGreetingsActive(false);
    }, []);
    var handleNextNavButtonClick = React.useCallback(function () {
        var nextSlideIdx = activeSlideIdx + 1;
        if (nextSlideIdx < slideGroupLength) {
            navigateNextSlide(nextSlideIdx);
        }
        else if (nextSlideIdx === slideGroupLength) {
            if (isSecondRun) {
                completeSecondRun();
            }
            else {
                completeFirstRun();
            }
        }
    }, [
        activeSlideIdx,
        completeFirstRun,
        completeSecondRun,
        navigateNextSlide,
        slideGroupLength,
        isSecondRun,
    ]);
    var onDismissClick = React.useCallback(function () {
        dismissSecondRunSlide();
    }, [dismissSecondRunSlide]);
    var handleBackNavButtonClick = React.useCallback(function (backNavButtonRef, nextNavButtonRef) {
        var newIdx = activeSlideIdx - 1;
        if (newIdx >= 0) {
            navigatePreviousSlide(newIdx);
        }
        if (newIdx === 0 &&
            document.activeElement === backNavButtonRef.current) {
            handleFocusButtonRef(nextNavButtonRef);
        }
    }, [activeSlideIdx, handleFocusButtonRef, navigatePreviousSlide]);
    var handleSetSlideAnnouncement = React.useCallback(function () {
        if (!config || !activeSlide) {
            return;
        }
        function formatText(str) {
            var redactStrArr = ['&lt;c0&gt;', '&lt;/c0&gt;'];
            for (var i = 0; i < redactStrArr.length; i++) {
                var redactStr = redactStrArr[i];
                var redactStrIndex = str.indexOf(redactStr);
                while (redactStrIndex !== -1) {
                    str = str.replace(redactStr, '');
                    redactStrIndex = str.indexOf(redactStr);
                }
            }
            return str;
        }
        var announcementArr = [];
        announcementArr.push(config.ariaLabels.nextSlideAnnouncementAriaLabel);
        announcementArr.push(formatText(activeSlide.headline));
        announcementArr.push(formatText(activeSlide.title));
        activeSlide.highlights.forEach(function (highlight) {
            announcementArr.push(formatText(highlight.text));
        });
        setSlideAnnouncement(announcementArr.join(' '));
    }, [activeSlide, config]);
    var handleRenderStart = React.useCallback(function (nextNavButtonRef) {
        handleFocusButtonRef(nextNavButtonRef);
        handleSetSlideAnnouncement();
        setSlideAnnouncementEnabled(true);
        handleBackgroundBlur(true);
        mark('First_Run_Ready');
    }, [handleFocusButtonRef, handleSetSlideAnnouncement]);
    var callbacks = React.useMemo(function () { return ({
        handleFocusButtonRef: handleFocusButtonRef,
        handleDisableGreetings: handleDisableGreetings,
        handleNextNavButtonClick: handleNextNavButtonClick,
        handleBackNavButtonClick: handleBackNavButtonClick,
        handleSetSlideAnnouncement: handleSetSlideAnnouncement,
        handleRenderStart: handleRenderStart,
        handleUpsellButtonClick: handleUpsellButtonClick,
        onDismissClick: onDismissClick,
    }); }, [
        handleFocusButtonRef,
        handleDisableGreetings,
        handleNextNavButtonClick,
        handleBackNavButtonClick,
        handleSetSlideAnnouncement,
        handleRenderStart,
        handleUpsellButtonClick,
        onDismissClick,
    ]);
    var firstRunModal = React.useMemo(function () {
        return config ? (React.createElement(FirstRunControl, { callbacks: callbacks, config: config, context: context, state: state, themeMode: themeMode, isSecondRun: isSecondRun })) : null;
    }, [callbacks, config, context, isSecondRun, state, themeMode]);
    return firstRunModal;
};
//# sourceMappingURL=FirstRunComponent.js.map